export type CameraDirection = "cw" | "ccw";

const BASE_POSITIONS_CW: Record<string, number> = {
  "Right Front": 1,
  "Right Side": 2,
  "Right Rear": 3,
  Rear: 4,
  "Left Rear": 5,
  "Left Side": 6,
  "Left Front": 7,
  Front: 8,
};

const BASE_POSITIONS_CCW: Record<string, number> = {
  "Left Front": 1,
  "Left Side": 2,
  "Left Rear": 3,
  Rear: 4,
  "Right Rear": 5,
  "Right Side": 6,
  "Right Front": 7,
  Front: 8,
};

const ROOF_POSITION = 999;
const TAG_SUFFIXES = [" Low", " High"];

const ABBREV_TO_TAG: Record<string, string> = {
  LF: "Left Front",
  LS: "Left Side",
  LR: "Left Rear",
  R: "Rear",
  RR: "Right Rear",
  RS: "Right Side",
  RF: "Right Front",
  F: "Front",
  T: "Roof",
};

const stripSuffix = (tag: string): string => {
  for (const suffix of TAG_SUFFIXES) {
    if (tag.endsWith(suffix)) return tag.slice(0, -suffix.length);
  }
  return tag;
};

/** Strip a "-letter" sub-position suffix used by featured-style names ("LF-a"). */
const stripLetterSuffix = (tag: string): string => {
  const dashIdx = tag.indexOf("-");
  return dashIdx >= 0 ? tag.slice(0, dashIdx) : tag;
};

/** Position 1..8 around the vehicle based on the camera tag and rotation direction.
 *  Accepts either full-name tags ("Left Front", "Right Rear High") or
 *  abbreviated forms with optional sub-position letter ("LF", "LF-a", "RR-b").
 *  Roof returns 999 so it always sorts last. Returns null for unknown / Unassigned. */
export const positionForCameraTag = (
  tag: string | null | undefined,
  direction: CameraDirection = "cw",
): number | null => {
  if (!tag || tag === "Unassigned" || tag === "N/A") return null;

  const trimmed = tag.trim();
  const baseRaw = stripLetterSuffix(trimmed);

  // Try abbreviation form (LF, RR, T, etc.)
  const expanded = ABBREV_TO_TAG[baseRaw.toUpperCase()];
  const base = expanded ?? stripSuffix(baseRaw);

  if (base === "Roof") return ROOF_POSITION;
  const table = direction === "ccw" ? BASE_POSITIONS_CCW : BASE_POSITIONS_CW;
  return table[base] ?? null;
};

/** Sub-position letter used to break ties at the same position
 *  (e.g. "LF-a" → "a"). Empty string when no suffix is present —
 *  empty sorts before "a" via localeCompare. */
export const letterForCameraTag = (tag: string | null | undefined): string => {
  if (!tag) return "";
  const dashIdx = tag.indexOf("-");
  return dashIdx >= 0 ? tag.slice(dashIdx + 1).trim().toLowerCase() : "";
};

/** Canonical CCW order used to derive a camera's stored `camera_position`
 *  number from its tag. CCW because product wants LFH = 1 (Left Front High).
 *
 *  Within each base location the priority is: High → base → Low. So when both
 *  LFH and LF exist, LFH gets the earlier slot; when LFH is absent, LF takes it.
 *  Roof always sorts last. Unassigned/unknown sort after Roof so they fall to the
 *  end of the list — they'll still get a position number, just the highest.
 *
 *  The number returned is for sort-ordering only; consumers should sort cameras
 *  by this rank and then assign sequential 1..N positions. */
const HIGH_OFFSET = -1;
const BASE_OFFSET = 0;
const LOW_OFFSET = 1;
const TAG_VARIANT_STRIDE = 10;
const ROOF_RANK = 9000;
const UNASSIGNED_RANK = 9999;

export const rankForCameraTag = (tag: string | null | undefined): number => {
  if (!tag || tag === "Unassigned" || tag === "N/A") return UNASSIGNED_RANK;

  const trimmed = tag.trim();
  const baseRaw = stripLetterSuffix(trimmed);

  // Detect High/Low suffix on the original tag (not the abbreviation path).
  let suffixOffset = BASE_OFFSET;
  let bareBase = baseRaw;
  if (baseRaw.endsWith(" High")) {
    suffixOffset = HIGH_OFFSET;
    bareBase = baseRaw.slice(0, -" High".length);
  } else if (baseRaw.endsWith(" Low")) {
    suffixOffset = LOW_OFFSET;
    bareBase = baseRaw.slice(0, -" Low".length);
  }

  // Accept abbreviations too ("LF", "LFH", "RR-a" — sub-letter already stripped).
  const expanded = ABBREV_TO_TAG[bareBase.toUpperCase()];
  const base = expanded ?? bareBase;

  if (base === "Roof") return ROOF_RANK + suffixOffset;

  const basePosition = BASE_POSITIONS_CCW[base];
  if (basePosition === undefined) return UNASSIGNED_RANK;

  return basePosition * TAG_VARIANT_STRIDE + suffixOffset;
};
